///
/// EarthBrowser - Data Table Component
///
/// Display tabular data with sorting support
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { ColumnMetadata, DataRow, DataValue, SortSpec } from "@/data-providers/types"
import { cn } from "@/utils/utils"
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

const cDefaultColumnMinWidth = 110

/**
 * Columns that fit the available width: drop the highest-priority-number
 * column until the estimated widths fit. Columns without a priority (or
 * priority 1) are never dropped, so un-annotated providers (CSV,
 * GeoPackage) keep every column and scroll horizontally instead.
 */
export function fitColumns(columns: ColumnMetadata[], width: number | null): { visible: ColumnMetadata[]; hidden: ColumnMetadata[] } {
	if (width === null || width <= 0) return { visible: columns, hidden: [] }
	const estimate = (c: ColumnMetadata) => c.minWidth ?? cDefaultColumnMinWidth
	const visible = [...columns]
	let total = visible.reduce((sum, c) => sum + estimate(c), 0)
	while (total > width) {
		let dropIndex = -1
		let dropPriority = 1
		visible.forEach((c, i) => {
			const p = c.priority ?? 1
			if (p > dropPriority) {
				dropPriority = p
				dropIndex = i
			}
		})
		if (dropIndex < 0) break
		total -= estimate(visible[dropIndex])
		visible.splice(dropIndex, 1)
	}
	return { visible, hidden: columns.filter(c => !visible.includes(c)) }
}

// Explicitly convert an arbitrary value to a display string
function stringifyValue(value: unknown): string {
	if (typeof value === 'string') return value
	if (value === null || value === undefined) return String(value)
	if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value)
	if (value instanceof Date) return value.toString()
	if (Array.isArray(value)) return value.map(v => stringifyValue(v)).join(',')
	return JSON.stringify(value)
}

// Convert an id-like value to a string key, or undefined if nullish
function rowKeyPart(value: DataValue | undefined): string | undefined {
	if (value === null || value === undefined) return undefined
	return stringifyValue(value)
}

export interface DataTableProps {
	// Data
	rows: DataRow[]
	columns: ColumnMetadata[]

	// Sorting
	sort?: SortSpec[]
	onSortChange?: (sort: SortSpec[]) => void

	// Row interaction
	onRowClick?: (row: DataRow) => void
	onRowDoubleClick?: (row: DataRow) => void
	selectedRowId?: string | number

	// Display options
	maxHeight?: string
	stickyHeader?: boolean
	striped?: boolean
	hoverable?: boolean

	// Loading state
	isLoading?: boolean

	// Empty state
	emptyMessage?: string
}

export function DataTable({
	rows,
	columns,
	sort = [],
	onSortChange,
	onRowClick,
	onRowDoubleClick,
	selectedRowId,
	maxHeight = "600px",
	stickyHeader = true,
	striped = true,
	hoverable = true,
	isLoading = false,
	emptyMessage = "No data available"
}: DataTableProps) {
	// Fit columns to the container (the data dock panel can be narrow).
	// Callback ref: the container only mounts after the loading/empty
	// branches, so a mount-time effect would never see it
	const observerRef = useRef<ResizeObserver | null>(null)
	const [containerWidth, setContainerWidth] = useState<number | null>(null)
	const containerRef = useCallback((el: HTMLDivElement | null) => {
		observerRef.current?.disconnect()
		observerRef.current = null
		if (!el) return
		const observer = new ResizeObserver(entries => {
			const width = entries[0]?.contentRect.width
			if (width !== undefined) setContainerWidth(Math.round(width))
		})
		observer.observe(el)
		observerRef.current = observer
	}, [])
	useEffect(() => () => observerRef.current?.disconnect(), [])
	const { visible: visibleColumns, hidden: hiddenColumns } = useMemo(
		() => fitColumns(columns, containerWidth),
		[columns, containerWidth]
	)
	// Providers that size every column opt into a fixed layout: columns take
	// their declared widths (scaled up to fill), long content truncates
	// instead of forcing the table wider, and the table only scrolls
	// horizontally when even the essential columns don't fit
	const fixedLayout = columns.length > 0 && columns.every(c => c.minWidth !== undefined)
	const fixedMinWidth = fixedLayout
		? visibleColumns.reduce((sum, c) => sum + (c.minWidth ?? cDefaultColumnMinWidth), 0)
		: undefined

	// Get sort direction for a column
	const getSortDirection = useCallback((columnId: string): 'asc' | 'desc' | null => {
		const sortSpec = sort.find(s => s.column === columnId)
		return sortSpec ? sortSpec.direction : null
	}, [sort])

	// Handle column header click
	const handleHeaderClick = useCallback((columnId: string) => {
		if (!onSortChange) return

		const currentDirection = getSortDirection(columnId)
		let newSort: SortSpec[]

		if (!currentDirection) {
			// Not sorted - sort ascending
			newSort = [{ column: columnId, direction: 'asc' }]
		} else if (currentDirection === 'asc') {
			// Currently ascending - sort descending
			newSort = [{ column: columnId, direction: 'desc' }]
		} else {
			// Currently descending - remove sort
			newSort = []
		}

		onSortChange(newSort)
	}, [getSortDirection, onSortChange])

	// Format cell value for display
	const formatValue = useCallback((value: unknown, column: ColumnMetadata): string => {
		if (value === null || value === undefined) {
			return '—'
		}

		if (column.type === 'date' && value instanceof Date) {
			return value.toLocaleString()
		}

		if (column.type === 'number' && typeof value === 'number') {
			return value.toLocaleString()
		}

		if (column.type === 'boolean') {
			return value ? '✓' : '✗'
		}

		if (column.type === 'geometry' || column.type === 'json') {
			return '[Object]'
		}

		return stringifyValue(value)
	}, [])

	// Render sort indicator
	const renderSortIndicator = useCallback((columnId: string) => {
		const direction = getSortDirection(columnId)

		if (!direction) {
			return <ChevronsUpDown className="w-3 h-3 text-white/30" />
		}

		return direction === 'asc'
			? <ArrowUp className="w-3 h-3 text-cyan-400" />
			: <ArrowDown className="w-3 h-3 text-cyan-400" />
	}, [getSortDirection])

	// Loading skeleton
	if (isLoading) {
		return (
			<div className="w-full h-full flex items-center justify-center">
				<div className="flex flex-col items-center gap-3">
					<div className="w-8 h-8 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
					<p className="text-sm text-white/60">Loading data...</p>
				</div>
			</div>
		)
	}

	// Empty state
	if (rows.length === 0) {
		return (
			<div className="w-full h-full flex items-center justify-center">
				<div className="text-center">
					<p className="text-white/60">{emptyMessage}</p>
				</div>
			</div>
		)
	}

	return (
		<div
			ref={containerRef}
			className="w-full h-full flex-1 min-h-0 overflow-auto"
			style={{ maxHeight }}
		>
			<table
				className={cn("w-full text-sm", fixedLayout ? "table-fixed" : "table-auto")}
				style={fixedMinWidth !== undefined ? { minWidth: fixedMinWidth } : undefined}
			>
				<thead
					className={cn(
						"bg-black/80 backdrop-blur-sm border-b border-white/10",
						stickyHeader ? "sticky top-0 z-[5]" : "invisible"
					)}
				>
					<tr>
						{visibleColumns.map(column => (
							<th
								key={column.id}
								className={cn(
									"px-3 py-2 text-left text-xs font-semibold text-white/80 whitespace-nowrap max-w-xs overflow-hidden",
									onSortChange && "cursor-pointer hover:bg-white/5 select-none"
								)}
								style={fixedLayout ? { width: column.minWidth } : undefined}
								onClick={() => handleHeaderClick(column.id)}
							>
								<div className="flex items-center gap-2 overflow-hidden">
									<span className="truncate">{column.name}</span>
									{onSortChange && renderSortIndicator(column.id)}
								</div>
							</th>
						))}
						{hiddenColumns.length > 0 && (
							<th
								className="px-2 py-2 text-right text-[10px] font-medium text-white/40 whitespace-nowrap w-px"
								title={`Hidden at this width: ${hiddenColumns.map(c => c.name).join(", ")}. Widen the panel to show them.`}
							>
								+{hiddenColumns.length}
							</th>
						)}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, rowIndex) => {
						const isSelected = selectedRowId !== undefined &&
							(row.id === selectedRowId || row._id === selectedRowId)

						return (
							<tr
								key={rowKeyPart(row.id) || rowKeyPart(row._id) || rowIndex}
								className={cn(
									"border-b border-white/5",
									striped && rowIndex % 2 === 1 && "bg-white/5",
									hoverable && "hover:bg-white/10 transition-colors",
									onRowClick && "cursor-pointer",
									isSelected && "bg-cyan-500/20 hover:bg-cyan-500/30"
								)}
								onClick={() => onRowClick?.(row)}
								onDoubleClick={() => onRowDoubleClick?.(row)}
							>
								{visibleColumns.map(column => {
									const cellContent = column.render
										? column.render(row[column.id], row)
										: formatValue(row[column.id], column)

									return (
										<td
											key={column.id}
											className={cn(
												"px-3 py-1.5 text-white/90 max-w-xs",
												fixedLayout && "overflow-hidden text-ellipsis whitespace-nowrap"
											)}
										>
											{typeof cellContent === 'string' ? (
												<div className="truncate" title={cellContent}>
													{cellContent}
												</div>
											) : (
												cellContent
											)}
										</td>
									)
								})}
								{hiddenColumns.length > 0 && <td className="w-px" />}
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

